import type {
  ZenviaTemplateCategory,
  ZenviaTemplateCreatePayload,
  ZenviaTemplatePatchPayload,
  ZenviaWhatsappTemplateComponents,
  ZenviaButtonItem,
} from '../../types/zenvia.types';
import { isLikelyButtonPayloadKey } from './clientTemplateFieldMap';
import { mapTemplateSenderIdToWizardValue } from './zenviaWhatsappSenders';

export type WizardStep = 1 | 2 | 3;

export type HeaderKind = 'none' | 'text' | 'image' | 'document' | 'video';

export type MessageLayout = 'simple' | 'full';

export type WizardButtonType = 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY';

export interface TemplateWizardButtonRow {
  type: WizardButtonType;
  text: string;
  url: string;
  phoneNumber: string;
  payload: string;
}

export interface TemplateWizardState {
  step: WizardStep;
  name: string;
  locale: string;
  category: ZenviaTemplateCategory;
  senderId: string;
  notificationEmail: string;
  messageLayout: MessageLayout;
  headerKind: HeaderKind;
  headerText: string;
  headerMediaUrl: string;
  bodyText: string;
  footerEnabled: boolean;
  footerText: string;
  buttonsEnabled: boolean;
  buttonRows: TemplateWizardButtonRow[];
  variableExamples: Record<string, string>;
}

const PLACEHOLDER_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

export function extractPlaceholders(...texts: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const t of texts) {
    if (!t) continue;
    const re = new RegExp(PLACEHOLDER_RE.source, 'g');
    let m: RegExpExecArray | null;
    while ((m = re.exec(t)) !== null) {
      const key = m[1].trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      order.push(key);
    }
  }
  return order;
}

function textHasVariables(text: string): boolean {
  return extractPlaceholders(text).length > 0;
}

function cleanExamples(ex: Record<string, string>): Record<string, string> | undefined {
  const o: Record<string, string> = {};
  for (const [k, v] of Object.entries(ex)) {
    const t = v.trim();
    if (t) o[k] = t;
  }
  return Object.keys(o).length ? o : undefined;
}

export function createInitialWizardState(): TemplateWizardState {
  return {
    step: 1,
    name: '',
    locale: 'pt_BR',
    category: 'MARKETING',
    senderId: '',
    notificationEmail: '',
    messageLayout: 'simple',
    headerKind: 'none',
    headerText: '',
    headerMediaUrl: '',
    bodyText: '',
    footerEnabled: false,
    footerText: '',
    buttonsEnabled: false,
    buttonRows: [{ type: 'URL', text: '', url: '', phoneNumber: '', payload: '' }],
    variableExamples: {},
  };
}

export function validateWizardState(state: TemplateWizardState): string | null {
  if (!state.name.trim()) return 'Informe o nome do modelo.';
  const nameOk = /^[a-z0-9_]+$/i.test(state.name.trim());
  if (!nameOk) return 'Use apenas letras, números e sublinhado no nome do modelo.';

  if (state.category !== 'AUTHENTICATION' && !state.bodyText.trim()) {
    return 'O corpo da mensagem é obrigatório para esta categoria.';
  }
  if (state.bodyText.length > 1024) return 'O corpo excede 1024 caracteres.';

  if (state.messageLayout === 'full') {
    if (state.headerKind === 'text') {
      if (!state.headerText.trim()) return 'Informe o texto do cabeçalho ou altere o tipo.';
      if (state.headerText.length > 60) return 'O cabeçalho de texto permite no máximo 60 caracteres.';
    }
    if (state.headerKind === 'image' || state.headerKind === 'document' || state.headerKind === 'video') {
      if (!state.headerMediaUrl.trim()) return 'Informe a URL pública do arquivo de mídia do cabeçalho.';
    }
    if (state.footerEnabled && !state.footerText.trim()) return 'Informe o texto do rodapé ou desative o rodapé.';
    if (state.footerEnabled && state.footerText.length > 60) return 'O rodapé permite no máximo 60 caracteres.';
    if (state.buttonsEnabled) {
      const rows = state.buttonRows.filter((r) => r.text.trim());
      if (rows.length === 0) return 'Adicione ao menos um botão com texto ou desative botões.';
      for (const r of rows) {
        if (r.type === 'URL' && !r.url.trim()) return 'Cada botão URL precisa de endereço.';
        if (r.type === 'PHONE_NUMBER' && !r.phoneNumber.trim()) return 'Cada botão de telefone precisa do número.';
        if (r.type === 'QUICK_REPLY' && !r.payload.trim()) return 'Cada resposta rápida precisa de payload.';
      }
    }
  }

  const textsForVars = [state.bodyText];
  if (state.messageLayout === 'full' && state.headerKind === 'text') {
    textsForVars.push(state.headerText);
  }
  const vars = extractPlaceholders(...textsForVars);
  for (const v of vars) {
    const ex = (state.variableExamples[v] ?? '').trim();
    if (!ex) return `Preencha o exemplo para a variável {{${v}}} (exigência Meta/Zenvia).`;
  }

  return null;
}

function buildButtonsBlock(state: TemplateWizardState): ZenviaWhatsappTemplateComponents['buttons'] | undefined {
  if (state.messageLayout !== 'full' || !state.buttonsEnabled) return undefined;
  const items: ZenviaButtonItem[] = [];
  for (const r of state.buttonRows) {
    if (!r.text.trim()) continue;
    if (r.type === 'URL') {
      items.push({ type: 'URL', text: r.text.trim(), url: r.url.trim() });
    } else if (r.type === 'PHONE_NUMBER') {
      items.push({ type: 'PHONE_NUMBER', text: r.text.trim(), phoneNumber: r.phoneNumber.trim() });
    } else {
      items.push({ type: 'QUICK_REPLY', text: r.text.trim(), payload: r.payload.trim() });
    }
  }
  if (items.length === 0) return undefined;
  return { type: 'MIXED', items };
}

export function buildZenviaWhatsappTemplatePayload(state: TemplateWizardState): ZenviaTemplateCreatePayload {
  const components: ZenviaWhatsappTemplateComponents = {};

  if (state.bodyText.trim() || state.category !== 'AUTHENTICATION') {
    const bt = state.bodyText;
    if (bt.trim()) {
      components.body = {
        type: textHasVariables(bt) ? 'TEXT_TEMPLATE' : 'TEXT_FIXED',
        text: bt,
      };
    }
  }

  if (state.messageLayout === 'full') {
    if (state.headerKind === 'text' && state.headerText.trim()) {
      components.header = {
        type: textHasVariables(state.headerText) ? 'TEXT_TEMPLATE' : 'TEXT_FIXED',
        text: state.headerText.trim(),
      };
    } else if (state.headerKind === 'image') {
      components.header = { type: 'MEDIA_IMAGE' };
    } else if (state.headerKind === 'document') {
      components.header = { type: 'MEDIA_DOCUMENT' };
    } else if (state.headerKind === 'video') {
      components.header = { type: 'MEDIA_VIDEO' };
    }

    if (state.footerEnabled && state.footerText.trim()) {
      components.footer = { type: 'TEXT_FIXED', text: state.footerText.trim() };
    }

    const btns = buildButtonsBlock(state);
    if (btns) components.buttons = btns;
  }

  const examples: Record<string, string> = { ...state.variableExamples };
  if (state.messageLayout === 'full') {
    const u = state.headerMediaUrl.trim();
    if (state.headerKind === 'image' && u) examples.imageUrl = u;
    if (state.headerKind === 'document' && u) examples.documentUrl = u;
    if (state.headerKind === 'video' && u) examples.videoUrl = u;
  }

  const payload: ZenviaTemplateCreatePayload = {
    name: state.name.trim(),
    locale: state.locale.trim() || 'pt_BR',
    channel: 'WHATSAPP',
    category: state.category,
    components,
  };

  if (state.senderId.trim()) payload.senderId = state.senderId.trim();
  if (state.notificationEmail.trim()) payload.notificationEmail = state.notificationEmail.trim();

  const exClean = cleanExamples(examples);
  if (exClean) payload.examples = exClean;

  return payload;
}

/** PATCH Zenvia: apenas locale, category, components, examples, notificationEmail (templates.partial-template). */
export function buildZenviaWhatsappTemplatePatchPayload(state: TemplateWizardState): ZenviaTemplatePatchPayload {
  const full = buildZenviaWhatsappTemplatePayload(state);
  const patch: ZenviaTemplatePatchPayload = {
    locale: full.locale,
    category: full.category,
    components: full.components,
    notificationEmail: (full.notificationEmail ?? '').trim(),
  };
  if (full.examples && Object.keys(full.examples).length > 0) patch.examples = full.examples;
  return patch;
}

function readString(obj: unknown, key: string): string {
  if (!obj || typeof obj !== 'object') return '';
  const v = (obj as Record<string, unknown>)[key];
  return v != null ? String(v) : '';
}

function parseHeader(
  header: unknown
): Pick<TemplateWizardState, 'headerKind' | 'headerText' | 'headerMediaUrl'> {
  if (!header || typeof header !== 'object') {
    return { headerKind: 'none', headerText: '', headerMediaUrl: '' };
  }
  const h = header as Record<string, unknown>;
  const type = String(h.type ?? '').toUpperCase();
  if (type === 'TEXT_FIXED' || type === 'TEXT_TEMPLATE') {
    return {
      headerKind: 'text',
      headerText: readString(h, 'text'),
      headerMediaUrl: '',
    };
  }
  if (type === 'MEDIA_IMAGE') {
    return { headerKind: 'image', headerText: '', headerMediaUrl: '' };
  }
  if (type === 'MEDIA_DOCUMENT') {
    return { headerKind: 'document', headerText: '', headerMediaUrl: '' };
  }
  if (type === 'MEDIA_VIDEO') {
    return { headerKind: 'video', headerText: '', headerMediaUrl: '' };
  }
  return { headerKind: 'none', headerText: '', headerMediaUrl: '' };
}

function parseButtons(buttons: unknown): Pick<TemplateWizardState, 'buttonsEnabled' | 'buttonRows'> {
  if (!buttons || typeof buttons !== 'object') {
    return {
      buttonsEnabled: false,
      buttonRows: [{ type: 'URL', text: '', url: '', phoneNumber: '', payload: '' }],
    };
  }
  const b = buttons as Record<string, unknown>;
  const items = b.items;
  if (!Array.isArray(items) || items.length === 0) {
    return {
      buttonsEnabled: false,
      buttonRows: [{ type: 'URL', text: '', url: '', phoneNumber: '', payload: '' }],
    };
  }
  const rows: TemplateWizardButtonRow[] = [];
  for (const it of items) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const t = String(o.type ?? '').toUpperCase();
    if (t === 'URL') {
      rows.push({
        type: 'URL',
        text: readString(o, 'text'),
        url: readString(o, 'url'),
        phoneNumber: '',
        payload: '',
      });
    } else if (t === 'PHONE_NUMBER') {
      rows.push({
        type: 'PHONE_NUMBER',
        text: readString(o, 'text'),
        url: '',
        phoneNumber: readString(o, 'phoneNumber'),
        payload: '',
      });
    } else if (t === 'QUICK_REPLY') {
      rows.push({
        type: 'QUICK_REPLY',
        text: readString(o, 'text'),
        url: '',
        phoneNumber: '',
        payload: readString(o, 'payload'),
      });
    }
  }
  if (rows.length === 0) {
    return {
      buttonsEnabled: false,
      buttonRows: [{ type: 'URL', text: '', url: '', phoneNumber: '', payload: '' }],
    };
  }
  return { buttonsEnabled: true, buttonRows: rows };
}

/**
 * Converte resposta GET da Zenvia para estado do wizard.
 * Retorna null se `components` for array legado ou formato não suportado.
 */
export function parseTemplateToWizardState(raw: Record<string, unknown>): TemplateWizardState | null {
  const comps = raw.components;
  if (!comps || typeof comps !== 'object' || Array.isArray(comps)) {
    return null;
  }
  const c = comps as Record<string, unknown>;

  const body = c.body;
  let bodyText = '';
  if (body && typeof body === 'object') {
    bodyText = readString(body, 'text');
  }

  const footer = c.footer;
  let footerEnabled = false;
  let footerText = '';
  if (footer && typeof footer === 'object') {
    footerText = readString(footer, 'text');
    footerEnabled = Boolean(footerText);
  }

  const headerParsed = parseHeader(c.header);
  const btnBlock = c.buttons;
  const hasBtnItems =
    btnBlock &&
    typeof btnBlock === 'object' &&
    Array.isArray((btnBlock as Record<string, unknown>).items) &&
    ((btnBlock as { items: unknown[] }).items?.length ?? 0) > 0;
  const hasHeader = c.header != null && typeof c.header === 'object';
  const messageLayout: MessageLayout =
    hasHeader || footerEnabled || hasBtnItems ? 'full' : 'simple';

  let headerKind = messageLayout === 'full' ? headerParsed.headerKind : 'none';
  let headerText = messageLayout === 'full' ? headerParsed.headerText : '';
  let headerMediaUrl = '';
  const ex = raw.examples;
  if (ex && typeof ex === 'object' && !Array.isArray(ex)) {
    const e = ex as Record<string, unknown>;
    if (headerKind === 'image') {
      headerMediaUrl = e.imageUrl != null ? String(e.imageUrl) : '';
    } else if (headerKind === 'document') {
      headerMediaUrl = e.documentUrl != null ? String(e.documentUrl) : '';
    } else if (headerKind === 'video') {
      headerMediaUrl = e.videoUrl != null ? String(e.videoUrl) : '';
    }
  }

  const btn = parseButtons(messageLayout === 'full' ? c.buttons : undefined);

  const catRaw = String(raw.category ?? 'MARKETING').toUpperCase();
  const allowed: ZenviaTemplateCategory[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
  const category = (allowed.includes(catRaw as ZenviaTemplateCategory)
    ? catRaw
    : 'MARKETING') as ZenviaTemplateCategory;

  const variableExamples: Record<string, string> = {};
  if (ex && typeof ex === 'object' && !Array.isArray(ex)) {
    const e = ex as Record<string, unknown>;
    const skip = new Set(['imageUrl', 'documentUrl', 'videoUrl']);
    for (const [k, v] of Object.entries(e)) {
      if (skip.has(k)) continue;
      if (v != null) variableExamples[k] = String(v);
    }
  }

  return {
    step: 1,
    name: String(raw.name ?? ''),
    locale: String(raw.locale ?? 'pt_BR'),
    category,
    senderId: mapTemplateSenderIdToWizardValue(
      raw.senderId != null ? String(raw.senderId) : ''
    ),
    notificationEmail:
      raw.notificationEmail != null ? String(raw.notificationEmail) : '',
    messageLayout,
    headerKind,
    headerText,
    headerMediaUrl,
    bodyText,
    footerEnabled,
    footerText,
    buttonsEnabled: btn.buttonsEnabled,
    buttonRows: btn.buttonRows,
    variableExamples,
  };
}

/** Sincroniza chaves de exemplo com placeholders atuais (mantém valores já preenchidos). */
export function syncVariableExamples(
  state: TemplateWizardState
): Record<string, string> {
  const texts = [state.bodyText];
  if (state.messageLayout === 'full' && state.headerKind === 'text') {
    texts.push(state.headerText);
  }
  const vars = extractPlaceholders(...texts);
  const next: Record<string, string> = {};
  for (const v of vars) {
    next[v] = state.variableExamples[v] ?? '';
  }
  return next;
}

const EXAMPLES_SKIP_KEYS = new Set(['imageUrl', 'documentUrl', 'videoUrl']);

/** Onde a variável foi inferida a partir do JSON do template (Zenvia/Meta). */
export type TemplateVariableOrigin =
  | 'api_registry'
  | 'body_text'
  | 'header_text'
  | 'button_payload'
  | 'examples_only';

export interface ClassifiedTemplateVariable {
  key: string;
  origins: TemplateVariableOrigin[];
}

/** Texto curto para badge na UI (PT). */
export const templateVariableOriginBadgePt: Record<TemplateVariableOrigin, string> = {
  api_registry: 'Registro API',
  body_text: 'Corpo',
  header_text: 'Cabeçalho',
  button_payload: 'Botão',
  examples_only: 'Exemplos',
};

/** Frase explicativa por origem (PT), para ajuda ao usuário. */
export const templateVariableOriginHelpPt: Record<TemplateVariableOrigin, string> = {
  api_registry:
    'Nome oficial que a plataforma espera no objeto fields ao disparar (lista fields do template na API).',
  body_text: 'Aparece como {{variável}} no texto principal do modelo (corpo da mensagem no WhatsApp).',
  header_text: 'Aparece como {{variável}} no texto do cabeçalho, quando o modelo tem cabeçalho de texto.',
  button_payload:
    'Valor do payload de botão de resposta rápida: você define no disparo (JSON/CSV); o cliente não envia esse texto ao tocar no botão.',
  examples_only:
    'Só encontramos esta chave nos exemplos retornados pela API (sem placeholder no texto analisado aqui). Use o mesmo nome no JSON ou CSV.',
};

/** Agrupamento visual: mensagem × botão × só metadados. */
export type TemplateVariableDisplayGroup = 'message' | 'button' | 'metadata';

const ORIGIN_SORT: TemplateVariableOrigin[] = [
  'api_registry',
  'header_text',
  'body_text',
  'button_payload',
  'examples_only',
];

function sortOriginsSet(s: Set<TemplateVariableOrigin>): TemplateVariableOrigin[] {
  return ORIGIN_SORT.filter((o) => s.has(o));
}

function addVariableOrigin(
  map: Map<string, Set<TemplateVariableOrigin>>,
  key: string,
  origin: TemplateVariableOrigin
): void {
  const t = key.trim();
  if (!t) return;
  if (!map.has(t)) map.set(t, new Set());
  map.get(t)!.add(origin);
}

/**
 * Classifica cada chave de variável do template por origem (corpo, cabeçalho, botão, API, exemplos).
 * A ordem das chaves é a mesma de {@link extractZenviaTemplateVariableKeys}.
 */
export function classifyZenviaTemplateVariableKeys(raw: unknown): ClassifiedTemplateVariable[] {
  const orderedKeys = extractZenviaTemplateVariableKeys(raw);
  if (orderedKeys.length === 0) return [];

  const originByKey = new Map<string, Set<TemplateVariableOrigin>>();

  if (!raw || typeof raw !== 'object') {
    return orderedKeys.map((key) => ({
      key,
      origins: (isLikelyButtonPayloadKey(key) ? ['button_payload'] : ['examples_only']) as TemplateVariableOrigin[],
    }));
  }

  const o = raw as Record<string, unknown>;

  const apiFields: string[] = [];
  if (Array.isArray(o.fields)) {
    for (const x of o.fields) {
      const t = String(x ?? '').trim();
      if (t) {
        apiFields.push(t);
        addVariableOrigin(originByKey, t, 'api_registry');
      }
    }
  }

  const rootText = typeof o.text === 'string' && o.text.trim() ? o.text : '';
  let bodyCompText = '';
  let headerCompText = '';
  const payloadTexts: string[] = [];

  const comps = o.components;
  if (comps && typeof comps === 'object' && !Array.isArray(comps)) {
    const c = comps as Record<string, unknown>;
    const body = c.body;
    if (body && typeof body === 'object') bodyCompText = readString(body, 'text');
    const header = c.header;
    if (header && typeof header === 'object') headerCompText = readString(header, 'text');
    const btnBlock = c.buttons;
    if (btnBlock && typeof btnBlock === 'object' && !Array.isArray(btnBlock)) {
      const items = (btnBlock as { items?: unknown[] }).items;
      if (Array.isArray(items)) {
        for (const it of items) {
          if (it && typeof it === 'object') {
            const o = it as Record<string, unknown>;
            const p = readString(o, 'payload');
            if (p.trim()) payloadTexts.push(p);
            const u = readString(o, 'url');
            if (u.trim()) payloadTexts.push(u);
          }
        }
      }
    }
  }

  for (const ph of extractPlaceholders(rootText)) addVariableOrigin(originByKey, ph, 'body_text');
  for (const ph of extractPlaceholders(bodyCompText)) addVariableOrigin(originByKey, ph, 'body_text');
  for (const ph of extractPlaceholders(headerCompText)) addVariableOrigin(originByKey, ph, 'header_text');
  for (const pt of payloadTexts) {
    for (const ph of extractPlaceholders(pt)) addVariableOrigin(originByKey, ph, 'button_payload');
  }

  const fromPlaceholdersMerged = extractPlaceholders(
    rootText,
    bodyCompText,
    headerCompText,
    ...payloadTexts
  );
  const fieldsArrayPresent = Array.isArray(o.fields);
  const skipOrphanExamples =
    fieldsArrayPresent && apiFields.length === 0 && fromPlaceholdersMerged.length === 0;

  const ex = o.examples;
  if (!skipOrphanExamples && ex && typeof ex === 'object' && !Array.isArray(ex)) {
    for (const k of Object.keys(ex as Record<string, unknown>)) {
      if (EXAMPLES_SKIP_KEYS.has(k)) continue;
      const t = k.trim();
      if (!t) continue;
      if (!originByKey.has(t)) addVariableOrigin(originByKey, t, 'examples_only');
    }
  }

  return orderedKeys.map((key) => {
    const set = originByKey.get(key);
    let origins = set ? sortOriginsSet(set) : [];
    if (origins.length === 0) {
      if (isLikelyButtonPayloadKey(key)) origins = ['button_payload'];
      else origins = ['examples_only'];
    }
    return { key, origins };
  });
}

export function templateVariableDisplayGroup(item: ClassifiedTemplateVariable): TemplateVariableDisplayGroup {
  const { key, origins } = item;
  const inMessage = origins.includes('header_text') || origins.includes('body_text');
  const inButton = origins.includes('button_payload') || isLikelyButtonPayloadKey(key);
  // Mesma chave no corpo e no payload do botão: um único valor em `fields` serve aos dois — mostrar no bloco "mensagem"
  // evita o usuário achar que só precisa tratar botões e acabar não preenchendo variável do texto.
  if (inMessage) return 'message';
  if (inButton) return 'button';
  return 'metadata';
}

/**
 * Chaves esperadas em `fields` ao enviar o template (disparo / teste).
 * 1) Array `fields` da resposta Zenvia (lista canônica, ex.: Meta import / API).
 * 2) Placeholders `{{...}}` em `text` raiz, body, header e payloads de botões.
 * 3) Chaves em `examples` (exceto mídia), exceto quando `fields` é `[]` e não há placeholder no texto
 *    (evita lixo tipo EMPRESAAQUI em template 100% TEXT_FIXED).
 */
export function extractZenviaTemplateVariableKeys(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;

  const apiFields: string[] = [];
  if (Array.isArray(o.fields)) {
    for (const x of o.fields) {
      const t = String(x ?? '').trim();
      if (t) apiFields.push(t);
    }
  }

  const texts: string[] = [];
  if (typeof o.text === 'string' && o.text.trim()) {
    texts.push(o.text);
  }

  const comps = o.components;
  if (comps && typeof comps === 'object' && !Array.isArray(comps)) {
    const c = comps as Record<string, unknown>;
    const body = c.body;
    if (body && typeof body === 'object') {
      texts.push(readString(body, 'text'));
    }
    const header = c.header;
    if (header && typeof header === 'object') {
      texts.push(readString(header, 'text'));
    }
    const btnBlock = c.buttons;
    if (btnBlock && typeof btnBlock === 'object' && !Array.isArray(btnBlock)) {
      const items = (btnBlock as { items?: unknown[] }).items;
      if (Array.isArray(items)) {
        for (const it of items) {
          if (it && typeof it === 'object') {
            const o = it as Record<string, unknown>;
            texts.push(readString(o, 'payload'));
            texts.push(readString(o, 'url'));
          }
        }
      }
    }
  }

  const fromPlaceholders = extractPlaceholders(...texts);

  const fieldsArrayPresent = Array.isArray(o.fields);
  const skipOrphanExamples =
    fieldsArrayPresent && apiFields.length === 0 && fromPlaceholders.length === 0;

  const fromExamples: string[] = [];
  const ex = o.examples;
  if (!skipOrphanExamples && ex && typeof ex === 'object' && !Array.isArray(ex)) {
    for (const k of Object.keys(ex as Record<string, unknown>)) {
      if (!EXAMPLES_SKIP_KEYS.has(k)) fromExamples.push(k);
    }
  }

  const seen = new Set<string>();
  const order: string[] = [];
  const pushUnique = (k: string) => {
    const t = k.trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    order.push(t);
  };

  for (const k of apiFields) pushUnique(k);
  for (const k of fromPlaceholders) pushUnique(k);
  for (const k of fromExamples) pushUnique(k);

  return order;
}
